import { useApolloClient, gql, Reference } from '@apollo/client';
import { isBefore, startOfDay } from 'date-fns';
import React, {
  ComponentProps,
  useRef,
  useCallback,
  useState,
  useEffect,
  useMemo,
} from 'react';
import { useTranslation } from 'react-i18next';
import {
  FlatList,
  TouchableOpacity,
  StyleSheet,
  Dimensions,
} from 'react-native';
import { useToast } from 'react-native-toast-notifications';

import { WebModal } from '@components/Modals/WebModal.web';
import { WebModalV3 } from '@components/Modals/WebModalV3.web';
import { ChecklistCommonModal } from '@components/ProjectDetail/ProjectChecklist/ChecklistCommonModal.web';
import { Box, Text } from '@components/Restyle';
import { CollapsableSectionListV3 } from '@components/shared/CollapsableSectionListV3.web';
import Icon from '@components/shared/Icon/RestyleIcon';
import { Skeleton } from '@components/shared/Skeleton';
import { SkeletonList, TaskRowSkeleton } from '@components/shared/skeletons';
import { MyTasksNewItem } from '@components/TaskList/MyTasksNewItem.web';
import { TaskCreatedToast } from '@components/TaskList/TaskCreatedToast.web';
import {
  TaskInviteOrAssignee,
  UserSearchType,
  StringSearchType,
} from '@components/TaskList/TaskInviteOrAssignee.web';
import { TaskListItemWeb } from '@components/TaskList/TaskListItem.web';
import CopyTaskDetails from '@components/Tasks/CopyTaskDetails';
import { NoTasksBanner } from '@components/Tasks/NoTasksBanner.web';
import { useWebDrawer } from '@components/Web/Drawer/WebDrawerContext';
import { TooltipOnboardingWeb } from '@components/Web/TooltipOnboarding.web';
import {
  Task,
  TaskGroup,
  Project,
  useDeleteTaskMutation,
  useOrderTasksMutation,
  TaskMemberRole,
  TaskSection,
  TaskStatus,
  useCompleteTaskMutation,
} from '@graphql/generated';
import useMe from '@hooks/useMe';
import { useGlobalPointerListener } from '@hooks/useOutsideClick.web';
import {
  resolveProjectTeamSubscription,
  useRefreshTeamSubscriptions,
} from '@hooks/useProjectTeamSubscription';
import { useAppNavigation } from '@navigation/useAppNavigation';
import documentIcons from '@themes/documentIcons';
import Images from '@themes/images';
import theme from '@themes/theme';
import { shouldShowPaywallForProject } from '@utils/teams/paywall';

const { width } = Dimensions.get('window');

type Header = {
  id: TaskGroup | 'COMPLETED';
  name: string;
  count: number;
  hasNextPage?: boolean;
  loading?: boolean;
};

type SectionData = {
  sectionHeader: Header;
  data: Task[];
  hasNextPage?: boolean;
};

export type TaskSectionNew = {
  sectionData: SectionData;
  loadMore?: () => void;
  members?: [];
  loading?: boolean;
};

export function resolveFirstAssignedTaskId(
  tasks: Task[] | TaskSectionNew[],
  grouped: boolean,
  meId: string | undefined
): string | null {
  // Guard: no known user → never produce a match. Without this, a task member
  // with an undefined user id would satisfy `undefined === undefined` and
  // return a false-positive assigned task id.
  if (!meId) return null;

  const isMyAssignedTask = (task: Task) =>
    task.members?.some(
      (m) => m.user?.id === meId && m.role === TaskMemberRole.Assignee
    ) ?? false;

  if (grouped) {
    const sections = tasks as TaskSectionNew[];
    for (const groupId of [TaskGroup.Current, TaskGroup.Overdue]) {
      const section = sections.find(
        (s) => s.sectionData.sectionHeader.id === groupId
      );
      const assigned = section?.sectionData.data.find(isMyAssignedTask);
      if (assigned) return assigned.id;
    }
    return null;
  }

  return (tasks as Task[]).find(isMyAssignedTask)?.id ?? null;
}

export interface TaskListProps {
  tasks: Task[] | TaskSectionNew[];
  onPress?: (task: Task, comeForm?: boolean) => void;
  refreshControl?: ComponentProps<typeof FlatList>['refreshControl'];
  onLoadMore?: () => void;
  isArrowVisible?: boolean;
  hideProject?: boolean;
  defaultProject?: Project;
  draggable?: boolean;
  showNoTasksBanner?: boolean;
  onBannerDismiss?: () => void;
  showFirstTaskTooltip?: boolean;
  onTooltipDismiss?: () => void;
}

export const MyTaskList = ({
  tasks,
  onPress,
  onLoadMore,
  hideProject,
  defaultProject,
  draggable = false,
  showNoTasksBanner = false,
  onBannerDismiss,
  showFirstTaskTooltip = false,
  onTooltipDismiss,
}: TaskListProps) => {
  const { t } = useTranslation('models');
  const navigation = useAppNavigation();
  const toast = useToast();
  const { me } = useMe();
  const {
    idTaskDetailOpen,
    isTaskWebPanelOpen,
    setPaywallModalOpen,
    setPaywallTeam,
  } = useWebDrawer();

  const [completeTask] = useCompleteTaskMutation();
  const [orderTasks] = useOrderTasksMutation();

  const delay = (ms: number) =>
    new Promise((resolve) => setTimeout(resolve, ms));

  const isTaskOverdue = (task: Task) => {
    if (!task.dueDate) return false;

    const due = new Date(task.dueDate);
    if (Number.isNaN(due.getTime())) return false;

    const todayStart = startOfDay(new Date());

    return isBefore(due, todayStart);
  };

  const client = useApolloClient();
  // TA-10958: keeps the rows' cache-first team billing reads fresh.
  useRefreshTeamSubscriptions();

  const mapTaskGroupToTaskSection = useCallback(
    (groupId: string): TaskSection | null => {
      switch (groupId) {
        case TaskGroup.Current:
          return TaskSection.Current;
        case TaskGroup.Overdue:
          return TaskSection.Overdue;
        case 'COMPLETED':
          return TaskSection.Completed;
        default:
          return null;
      }
    },
    []
  );

  const moveTaskInPaginatedListTasksAfterDelay = (
    cache: any,
    task: Task,
    willComplete: boolean
  ) => {
    const taskId = task.id;

    const entityId = cache.identify({
      __typename: 'Task',
      id: taskId,
    });

    // Safety guard: if entity is not in cache, do nothing
    if (!entityId) return;

    const taskRef: Reference = { __ref: entityId };

    const makeEdge = () => ({
      __typename: 'TaskEdge',
      cursor: '',
      node: taskRef,
    });

    const removeEdge = (edges: any[]) =>
      (edges ?? []).filter((e: any) => e?.node.__ref !== 'Task:' + taskId);

    const appendEdge = (edges: any[]) => {
      const list = edges ?? [];
      const exists = list.some((e: any) => e?.node.__ref === 'Task:' + taskId);
      return exists ? list : [...list, makeEdge()];
    };

    cache.modify({
      id: 'ROOT_QUERY',
      fields: {
        listTasks(existingConn: any, helpers: any) {
          const { storeFieldName } = helpers;
          if (!existingConn?.edges) return existingConn;

          const isOverdue = storeFieldName.includes('"dueGroup":"OVERDUE"');

          const isCurrent = storeFieldName.includes('"dueGroup":"CURRENT"');

          const isCompleted = storeFieldName.includes('"status":"COMPLETED"');

          const is1 = isTaskOverdue(task);
          const isActiveList = (is1 && isOverdue) || (!is1 && isCurrent);

          if (isActiveList) {
            return {
              ...existingConn,
              edges: willComplete
                ? removeEdge(existingConn.edges)
                : appendEdge(existingConn.edges),
            };
          }

          if (isCompleted) {
            return {
              ...existingConn,
              edges: willComplete
                ? appendEdge(existingConn.edges)
                : removeEdge(existingConn.edges),
            };
          }

          return existingConn;
        },
      },
    });
  };

  const handleCompleteTask = async (task: Task) => {
    const willComplete = task.completedAt === null;

    // // 1) Instant local UI update (entity-level)
    client.cache.writeFragment({
      id: client.cache.identify({ __typename: 'Task', id: task.id }),
      fragment: gql`
        fragment _TaskComplete on Task {
          completedAt
          status
        }
      `,
      data: {
        completedAt: willComplete ? new Date().toISOString() : null,
        status: willComplete ? TaskStatus.Completed : TaskStatus.Ongoing,
      },
    });

    // 2) Delayed list move (pagination-safe)
    setTimeout(() => {
      moveTaskInPaginatedListTasksAfterDelay(client.cache, task, willComplete);
    }, 700);

    // 3) Fire mutation (do NOT refetch listTasks)
    await completeTask({
      variables: { id: task.id },
    });
  };
  // Use a typed ref for ResizeObserver target (RN Web ref -> DOM element).
  const listTableRef = useRef<HTMLElement | null>(null);

  // A tick counter is more semantically correct than toggling a boolean.
  const [layoutTick, setLayoutTick] = useState(0);

  const [selectedTaskId, setSelectedTaskId] = useState<string | undefined>();
  const [selectedUserList, setSelectedUserList] = useState<UserSearchType[]>(
    []
  );
  const [showInviteOrAssignModal, setShowInviteOrAssignModal] = useState(false);
  const [duplicateTaskId, setDuplicateTaskId] = useState<string | undefined>(
    undefined
  );
  const [focusOutside, setFocusOutside] = useState<boolean>(false);
  const handleDocMouseDown = useCallback((e: MouseEvent) => {
    const el = listTableRef.current;
    const target = e.target;

    setFocusOutside(!el || !target || !el.contains(target));
  }, []);

  useGlobalPointerListener('mousedown', handleDocMouseDown);
  useEffect(() => {
    if (focusOutside && showFirstTaskTooltip) {
      onTooltipDismiss?.();
    }
  }, [focusOutside, showFirstTaskTooltip, onTooltipDismiss]);

  const [deleteAlertModal, setDeleteAlertModal] = useState(false);
  const [tasksToHandle, setTasksToHandle] = useState<Task[]>([]);
  // Explicit local flag for the whole delete batch, NOT useDeleteTaskMutation's
  // own `loading` — a multi-select delete fires one deleteTask() call per task
  // via Promise.all, and Apollo's useMutation only tracks the most recently
  // *dispatched* call's own lifecycle (mutationId-guarded), so its `loading`
  // can flip back to false as soon as that one call settles even while
  // earlier-dispatched calls in the same batch are still in flight — Copilot
  // review.
  const [isDeletingTasks, setIsDeletingTasks] = useState(false);

  const [deleteTask] = useDeleteTaskMutation({
    // Keep existing behavior. If you want to remove refetch here too,
    // we can update cache similarly to invitations.
    refetchQueries: ['listTasks'],
    // Code review fix: doDeleteTasks's own .catch() below already shows a
    // "Failed to delete" toast for any error — opt out of the global
    // PERMISSION_DENIED/CONFLICT toast so this doesn't double up. Only this
    // call site opts out; deleteTask's other call sites (useTaskDetailMenuActions.ts,
    // task-detail-tabs.web.tsx) are unaffected and still get the global toast.
    context: { suppressGlobalMutationErrorToast: true },
  });

  // Determine whether tasks are grouped.
  const grouped = useMemo(() => {
    return (
      Array.isArray(tasks) &&
      (tasks as TaskSectionNew[])[0]?.sectionData !== undefined
    );
  }, [tasks]);

  // Memoize section data so CollapsableSectionListV3 doesn't receive
  // a new array instance every render.
  const sectionDataList = useMemo(() => {
    if (!grouped) return [];
    return (tasks as TaskSectionNew[]).map((d) => {
      // `loading` lives on the TaskSectionNew wrapper (d.loading), not on
      // d.sectionData — reading it here instead of leaving it undefined.
      // Gated on data.length === 0 so a background refetch of an
      // already-loaded section never flashes a skeleton/spinner over real
      // rows (same rule as isInitialTaskListLoading below).
      const isSectionLoading = !!d.loading && d.sectionData.data.length === 0;
      return {
        ...d.sectionData,
        loading: isSectionLoading,
        sectionHeader: {
          ...d.sectionData.sectionHeader,
          hasNextPage: d.sectionData.hasNextPage,
          loading: isSectionLoading,
        },
      };
    });
  }, [grouped, tasks]);

  // Flat tasks memo.
  const flatTasks = useMemo(() => {
    if (grouped) return [];
    return tasks as Task[];
  }, [grouped, tasks]);

  // True only on the very first load (every section still loading with no
  // data yet) — never during a debounce/filter change that already has
  // cached rows to show, and never once any section has resolved data.
  // Prevents "No current tasks" etc. from flashing before the query settles
  // (TA-10273 global rule: never show an empty state while a query is in
  // flight).
  const isInitialTaskListLoading = useMemo(
    () =>
      grouped &&
      sectionDataList.length > 0 &&
      sectionDataList.every((s) => s.loading),
    [grouped, sectionDataList]
  );

  // Keep selectedTaskId in sync with opened task detail panel.
  useEffect(() => {
    if (
      isTaskWebPanelOpen &&
      idTaskDetailOpen !== '' &&
      idTaskDetailOpen !== selectedTaskId
    ) {
      setSelectedTaskId(idTaskDetailOpen);
    }
  }, [isTaskWebPanelOpen, idTaskDetailOpen, selectedTaskId]);

  // Observe container resize (RN Web -> DOM element).
  useEffect(() => {
    const el = listTableRef.current;
    if (!el) return;

    const resizeObserver = new ResizeObserver(() => {
      setLayoutTick((n) => n + 1);
    });

    resizeObserver.observe(el);
    return () => resizeObserver.disconnect();
  }, []);

  // Centralized toast helper; memoize to avoid re-creating on every render.
  const showToast = useCallback(
    (
      onPressCb: () => void,
      onCloseCb: () => void,
      title: string | undefined,
      msg: string | undefined,
      iconName?: keyof typeof Images | keyof typeof documentIcons
    ) => {
      toast.hideAll();
      return toast.show(
        <TaskCreatedToast
          title={title ?? 'Task Completed'}
          iconName={iconName}
          message={msg}
          duration={3000}
          onPress={onPressCb}
        />,
        {
          type: 'custom',
          duration: 3000,
          placement: 'bottom',
          animationType: 'slide-in',
          style: {
            marginBottom: 39,
            backgroundColor: 'transparent',
            shadowColor: 'transparent',
          },
          onClose: onCloseCb,
        }
      );
    },
    [toast]
  );

  // Section header renderer (kept as a simple function; can be useCallback if needed).
  const sectionHeader = useCallback((item: Header) => {
    return (
      <Box style={styles.headerMainContainer}>
        <Box
          flexDirection='row'
          flex={width < 1025 ? 1.1 : 2.7}
          alignSelf='flex-start'
          alignItems='center'>
          <Text color='textSecondary' variant='labelEmphasized' mr='xs'>
            {item.name}
          </Text>

          {item.loading && item.id !== 'COMPLETED' ? (
            <Skeleton
              testID={`task-section-count-skeleton-${item.id}`}
              variant='circle'
              width={20}
              height={20}
            />
          ) : (
            item.count > 0 &&
            item.id !== 'COMPLETED' && (
              <Box
                alignItems='center'
                justifyContent='center'
                borderColor='grey02'
                borderWidth={1}
                borderRadius='s'
                height={20}
                minWidth={20}>
                <Text
                  mx='xxxs'
                  color='grey04'
                  variant={
                    width < 1025
                      ? 'labelEmphasizedResponsive'
                      : 'labelTabHeaderText'
                  }>
                  {item.count}
                  {item.hasNextPage ? '+' : ''}
                </Text>
              </Box>
            )
          )}
        </Box>
      </Box>
    );
  }, []);

  // First task in Current or Overdue section where I am an Assignee.
  const firstAssignedTaskId = useMemo(
    () => resolveFirstAssignedTaskId(tasks, grouped, me?.id),
    [tasks, grouped, me?.id]
  );

  // If the tooltip should show but the assigned task is not yet in the loaded
  // section data (it's on a next page), auto-load more pages for Current then
  // Overdue until the task appears or there are no more pages.
  useEffect(() => {
    if (!showFirstTaskTooltip || firstAssignedTaskId !== null || !grouped)
      return;
    const sections = tasks as TaskSectionNew[];
    const sectionToLoad = sections
      .filter((s) =>
        ([TaskGroup.Current, TaskGroup.Overdue] as string[]).includes(
          s.sectionData.sectionHeader.id
        )
      )
      .find((s) => s.sectionData.hasNextPage && s.loadMore);
    sectionToLoad?.loadMore?.();
  }, [showFirstTaskTooltip, firstAssignedTaskId, tasks, grouped]);

  // Section IDs that should start expanded. When the first-task tooltip is
  // active, expand both Current and Overdue so the assigned task row is in the
  // DOM with correct layout from the start — the Overdue section starts
  // collapsed by default, which causes getBoundingClientRect to return wrong
  // coordinates for the tooltip portal.
  const initialExpandedSectionIds = useMemo(
    () =>
      showFirstTaskTooltip
        ? ([TaskGroup.Current, TaskGroup.Overdue] as string[])
        : undefined,
    [showFirstTaskTooltip]
  );

  const renderSectionContentHeader = useCallback(
    (item: Header) => {
      if (item.id !== TaskGroup.Current) return null;
      if (showNoTasksBanner && onBannerDismiss) {
        return <NoTasksBanner onDismiss={onBannerDismiss} />;
      }
      return null;
    },
    [showNoTasksBanner, onBannerDismiss]
  );

  const renderEmpty = useCallback((id: string) => {
    let title: string | undefined;
    let subTitle: string | undefined;

    switch (id) {
      case TaskGroup.Current:
        title = 'No current tasks';
        break;
      case TaskGroup.Overdue:
        title = 'No overdue tasks';
        subTitle = 'Nice work! Nothing is past due';
        break;
      case 'COMPLETED':
        title = 'No completed tasks yet';
        subTitle = 'Finish a task to see it here';
        break;
    }

    if (!title) return <></>;

    return (
      <Box m='m' alignItems='center'>
        <Text variant='webBodySecondary'>{title}</Text>
        {subTitle && (
          <Text variant='webBodyHintSubline' mt='xxs'>
            {subTitle}
          </Text>
        )}
      </Box>
    );
  }, []);

  // Type guard for grouped tasks.
  const tasksAreGrouped = useCallback(
    (input: Task[] | TaskSectionNew[]): input is TaskSectionNew[] => {
      return (input as TaskSectionNew[])[0]?.sectionData !== undefined;
    },
    []
  );

  const getLoadMoreDataFuction = useCallback(
    (header?: Header) => {
      if (tasksAreGrouped(tasks)) {
        const taskSections = tasks as TaskSectionNew[];
        const taskSection = taskSections.find(
          (x) => x.sectionData.sectionHeader.id === header?.id
        );

        const { loadMore, sectionData } = taskSection || {
          loadMore: undefined,
          sectionData: undefined,
        };

        // NOTE: hasNextPage is currently pulled from sectionData via `as any` in your codebase.
        if ((sectionData as any)?.hasNextPage) {
          return loadMore;
        }

        return undefined;
      }

      return onLoadMore || undefined;
    },
    [tasks, onLoadMore, tasksAreGrouped]
  );

  const renderLoadMoreComponent = useCallback(
    (header?: Header) => {
      const loadMoreData = getLoadMoreDataFuction(header);
      if (!loadMoreData) return null;

      return (
        <Box alignItems='center'>
          <TouchableOpacity
            accessibilityLabel={t('shared:loadMore')}
            onPress={loadMoreData}>
            <Box
              marginVertical='s'
              backgroundColor='grey01'
              p='s'
              borderRadius='xs'
              alignSelf='flex-start'>
              <Text variant='labelSmall' textAlign='center'>
                {t('shared:loadMore')}
              </Text>
            </Box>
          </TouchableOpacity>
        </Box>
      );
    },
    [getLoadMoreDataFuction, t]
  );

  const handleSectionEndReached = useCallback(
    async (header: Header) => {
      const loadMoreData = getLoadMoreDataFuction(header);
      if (loadMoreData) await loadMoreData();
    },
    [getLoadMoreDataFuction]
  );

  const renderTaskInvitationModal = useCallback(() => {
    if (!showInviteOrAssignModal) return null;
    if (!tasksToHandle?.[0]?.id) return null;

    return (
      <WebModalV3
        canOverflowModal={true}
        accessibilityLabel='Assign Task Member'
        onClose={() => {
          setShowInviteOrAssignModal(false);
        }}
        visible={showInviteOrAssignModal}
        width={800}
        height={412}>
        <TaskInviteOrAssignee
          taskIdList={tasksToHandle.map((t) => t.id)}
          selectedUser={selectedUserList}
          onClose={() => {
            setShowInviteOrAssignModal(false);
          }}
        />
      </WebModalV3>
    );
  }, [showInviteOrAssignModal, tasksToHandle, selectedUserList]);

  const handleTaskPress = useCallback(
    (task: Task) => {
      setSelectedTaskId(task.id);
      setTimeout(() => {
        onPress?.(task);
      }, 500);
    },
    [onPress]
  );

  const handleToggleCompleteTask = useCallback(
    (task: Task) => {
      handleCompleteTask(task);
      delay(900).then(() => {
        const toastId = showToast(
          () => {
            setTimeout(() => toast.hide(toastId), 0);
          },
          () => {},
          task.completedAt === null
            ? 'Task Completed'
            : `Task marked as incomplete`,
          undefined,
          task.completedAt === null ? 'CheckCircle' : 'Circle'
        );
      });
    },
    [handleCompleteTask, showToast, toast]
  );

  // Render TaskListItemWeb in a stable callback to reduce re-renders.
  const renderTaskItem = useCallback(
    (task: Task, isTheLastOne: boolean, isSelected: boolean) => {
      const showTooltipHere =
        showFirstTaskTooltip &&
        !!onTooltipDismiss &&
        task.id === firstAssignedTaskId;

      const taskItem = (
        <TaskListItemWeb
          focusOutside={focusOutside}
          isSelected={isSelected}
          draggable={draggable}
          hasBorder={!isTheLastOne}
          item={task}
          navigation={navigation}
          hideProject={hideProject}
          onPress={handleTaskPress}
          toggleCompleteTask={handleToggleCompleteTask}
        />
      );

      if (!showTooltipHere) return taskItem;

      return (
        <TooltipOnboardingWeb
          variant='bottom-left'
          tooltipStyle='success'
          title='You have tasks waiting'
          description='Tap task to see what needs to be done.'
          forceShow
          trigger='press'
          offset={0}
          horizontalOffset={64}
          style={{ alignSelf: 'stretch' }}>
          {taskItem}
        </TooltipOnboardingWeb>
      );
    },
    [
      navigation,
      handleTaskPress,
      handleToggleCompleteTask,
      hideProject,
      draggable,
      focusOutside,
      showFirstTaskTooltip,
      onTooltipDismiss,
      firstAssignedTaskId,
    ]
  );

  // FlatList version of renderItem (only used when not grouped).
  const renderFlatListItem = useCallback(
    ({ item, index }: { item: Task; index: number }) => (
      <Box mx='m'>
        {renderTaskItem(item, index === flatTasks.length - 1, false)}
      </Box>
    ),
    [renderTaskItem, flatTasks.length]
  );

  const doDeleteTasks = useCallback(() => {
    setIsDeletingTasks(true);
    Promise.all(
      Array.from(tasksToHandle).map(({ id }) =>
        deleteTask({
          variables: { id },
        })
      )
    )
      .then((results) => {
        // TA-10139 bug fix: with the client's global `errorPolicy: 'all'`,
        // a GraphQL business error resolves deleteTask() with
        // `{ errors: [...] }` instead of rejecting — the old `.catch()`
        // below never saw it, so a real backend failure looked identical
        // to success (modal closed, "Deleted" toast shown, nothing
        // actually deleted) — a silent regression of the TA-10274 QA fix,
        // which depends on `.catch()` firing on failure.
        const hasErrors = results.some(
          (result) => (result?.errors as unknown[] | undefined)?.length
        );
        if (hasErrors) {
          throw new Error('One or more task deletions failed');
        }

        if (tasksToHandle.length === 1) {
          const toastId = showToast(
            () => {
              setTimeout(() => toast.hide(toastId), 0);
            },
            () => {},
            'Task Deleted',
            tasksToHandle[0].name
          );
        } else {
          const toastId = showToast(
            () => {
              setTimeout(() => toast.hide(toastId), 0);
            },
            () => {},
            `${tasksToHandle.length} Tasks Deleted`,
            undefined
          );
        }

        setDeleteAlertModal(false);
        setTasksToHandle([]);
      })
      .catch(() => {
        // Keep the modal open on failure — QA finding (TA-10274): the
        // confirmation modal must stay locked/open until the server
        // confirms, and a failure must be represented, not swallowed
        // silently while the modal has already closed.
        const toastId = showToast(
          () => {
            setTimeout(() => toast.hide(toastId), 0);
          },
          () => {},
          'Failed to delete',
          tasksToHandle.length === 1 ? tasksToHandle[0].name : undefined
        );
      })
      .finally(() => {
        setIsDeletingTasks(false);
      });
  }, [tasksToHandle, deleteTask, showToast, toast]);

  const onRightMenuAction = useCallback(
    async (
      actionType:
        | 'complete'
        | 'assignee'
        | 'duplicate'
        | 'moveUp'
        | 'moveDown'
        | 'delete',
      selectedTasks: Task[]
    ) => {
      if (selectedTasks.length === 0) return;

      const first = selectedTasks[0];

      // TA-10958: listTasks rows carry no team billing. Resolve it before the
      // paywall check, and refuse the action when it cannot be resolved,
      // since a check on missing billing answers "no paywall".
      const resolvePaywallProject = async () => {
        const { project, billingPending } =
          await resolveProjectTeamSubscription(client, first.project);
        if (billingPending) {
          const toastId = showToast(
            () => {
              setTimeout(() => toast.hide(toastId), 0);
            },
            () => {},
            "Couldn't check your team plan",
            'Please try again.',
            'AlertCircle'
          );
          return { project, blocked: true };
        }
        return { project, blocked: false };
      };

      switch (actionType) {
        case 'complete': {
          Promise.all(
            selectedTasks.map((taskParam) => handleCompleteTask(taskParam))
          ).then(() => {
            const toastId = showToast(
              () => {
                setTimeout(() => toast.hide(toastId), 0);
              },
              () => {},
              first.completedAt === null
                ? 'Task Completed'
                : `Task${
                    selectedTasks.length > 1 ? 's' : ''
                  } marked as incomplete`,
              undefined,
              first.completedAt === null ? 'CheckCircle' : 'Circle'
            );
          });
          break;
        }

        case 'duplicate': {
          // Check paywall before duplicating
          const { project: firstProject, blocked } =
            await resolvePaywallProject();
          if (blocked) break;
          if (firstProject && shouldShowPaywallForProject(firstProject)) {
            setPaywallTeam(firstProject?.team ?? undefined);
            setPaywallModalOpen(true);
            break;
          }
          setDuplicateTaskId(first.id);
          break;
        }

        case 'assignee': {
          // Check paywall before opening assignee modal
          const { project: firstProject, blocked } =
            await resolvePaywallProject();
          if (blocked) break;
          if (firstProject && shouldShowPaywallForProject(firstProject)) {
            setPaywallTeam(firstProject?.team ?? undefined);
            setPaywallModalOpen(true);
            break;
          }

          // Build a unique list of selected members + invitations.
          // Use a map for O(1) dedupe and deterministic role upgrade logic.
          const map = new Map<string, UserSearchType>();

          for (const task of selectedTasks) {
            // Existing members
            for (const tm of task.members ?? []) {
              if (tm.role === TaskMemberRole.Owner) continue;
              if (tm.user?.id === me?.id) continue;

              const key = `user:${tm.user.id}`;
              const existing = map.get(key);

              const candidate: UserSearchType = {
                ...tm.user,
                role: tm.role,
              };

              // Prefer Assignee over Viewer when duplicates exist.
              if (!existing) {
                map.set(key, candidate);
              } else if (
                existing.role === TaskMemberRole.Viewer &&
                tm.role === TaskMemberRole.Assignee
              ) {
                map.set(key, candidate);
              }
            }

            // Invitations
            for (const inv of task.invitations ?? []) {
              const key = `inv:${inv.email}`;
              const existing = map.get(key);

              const candidate: StringSearchType = {
                title: inv.email,
                role: inv.role,
                expiredAt: inv.expiredAt,
                id: inv.id,
                __typename: 'StringSearchType',
              };

              if (!existing) {
                map.set(key, candidate);
              } else if (
                existing.role === TaskMemberRole.Viewer &&
                inv.role === TaskMemberRole.Assignee
              ) {
                map.set(key, candidate);
              }
            }
          }

          setSelectedUserList(Array.from(map.values()));
          setTasksToHandle(selectedTasks);
          setShowInviteOrAssignModal(true);
          break;
        }

        case 'delete': {
          setDeleteAlertModal(true);
          setTasksToHandle(selectedTasks);
          break;
        }

        case 'moveDown':
        case 'moveUp':
        default: {
          break;
        }
      }
    },
    [me?.id, showToast, toast, handleCompleteTask, client]
  );

  return (
    <>
      {renderTaskInvitationModal()}

      {deleteAlertModal && (
        <ChecklistCommonModal
          title={t('models:deleteTask.confirmTitle')}
          // TA-10949 #13/#14 (review round 2): the ticket's verbatim copy
          // only covers the single-task case — pluralize for multi-select
          // via i18next's count-based _one/_other keys, same convention
          // already used elsewhere in this file (see tabHeading_other).
          message={t('models:deleteTask.confirmMessage', {
            count: tasksToHandle.length,
          })}
          buttonTitle={t('models:deleteTask.confirmButton')}
          visible={deleteAlertModal}
          // Only the multi-select case shows the item list (design has no
          // bullet for a single task, see task-detail-tabs.web.tsx's Delete
          // for the sidebar/single-task entry point sharing this same modal).
          skippedItems={
            tasksToHandle.length > 1 ? tasksToHandle.map((x) => x.name) : []
          }
          isLoading={isDeletingTasks}
          onPressDelete={doDeleteTasks}
          messageEnd={t('models:deleteTask.confirmMessageEnd', {
            count: tasksToHandle.length,
          })}
          onClose={() => {
            setDeleteAlertModal(false);
            setTasksToHandle([]);
          }}
        />
      )}

      {!!duplicateTaskId && (
        <WebModal
          accessibilityLabel='Duplicate Task'
          visible={duplicateTaskId}
          title={t('shared:select')}
          width={504}
          onClose={() => {
            setDuplicateTaskId(undefined);
          }}>
          <CopyTaskDetails
            taskId={duplicateTaskId}
            onComplete={(task) => {
              setDuplicateTaskId(undefined);
              const toastId = showToast(
                () => {
                  onPress?.(task, false);
                  setTimeout(() => toast.hide(toastId), 0);
                },
                () => {},
                'Duplicated Task',
                task.name
              );
            }}
          />
        </WebModal>
      )}

      <Box
        marginTop='xxs'
        flex={1}
        ref={listTableRef as any}
        style={styles.mainListContainer}>
        {grouped && isInitialTaskListLoading && (
          <Box testID='task-list-skeleton'>
            {sectionDataList.map((section) => (
              // Mirrors CollapsableSectionListV3's own bordered row chrome
              // (borderWidth/mx/mb/borderRadius + 52px header row) so the
              // skeleton doesn't visually swap to a completely different
              // container once the real list mounts.
              <Box
                key={section.sectionHeader.id}
                borderWidth={1}
                mb='m'
                mx='m'
                borderColor='grey02'
                borderRadius='xs'
                backgroundColor='white'>
                <Box
                  height={52}
                  flexDirection='row'
                  alignItems='center'
                  paddingLeft='m'
                  style={{
                    paddingRight: theme.spacing.m + theme.spacing.l,
                  }}>
                  {sectionHeader(section.sectionHeader)}
                  <Icon variant='l' name='ChevronDown' color='grey05' />
                </Box>
                <Box
                  mb={
                    section.sectionHeader.id !== TaskGroup.Current
                      ? 'xs'
                      : 'none'
                  }>
                  <SkeletonList
                    row={(index) => <TaskRowSkeleton key={index} />}
                  />
                </Box>
              </Box>
            ))}
          </Box>
        )}
        {grouped && !isInitialTaskListLoading && (
          <CollapsableSectionListV3<Header, Task>
            key={`section-list-${showFirstTaskTooltip}`}
            initialExpandedSectionIds={initialExpandedSectionIds}
            draggable={draggable}
            onRightMenuAction={onRightMenuAction}
            renderSectionContentHeader={
              showNoTasksBanner && onBannerDismiss
                ? renderSectionContentHeader
                : undefined
            }
            data={sectionDataList}
            onOrderChange={async ({
              sectionHeader,
              oldData,
              newData,
              movedIds,
              afterId,
              beforeId,
            }) => {
              const oldIds = oldData.map((task) => task.id);
              const newIds = newData.map((task) => task.id);
              if (
                oldIds.length === newIds.length &&
                oldIds.every((id, index) => id === newIds[index])
              ) {
                return;
              }

              const section = mapTaskGroupToTaskSection(sectionHeader.id);
              if (!section || movedIds.length === 0) {
                return;
              }

              let mutationError: Error | null = null;
              await orderTasks({
                variables: {
                  taskIds: movedIds,
                  section,
                  afterTaskId: afterId,
                  beforeTaskId: beforeId,
                },
                onCompleted: () => {
                  delay(100).then(() => {
                    const toastId = showToast(
                      () => {
                        setTimeout(() => toast.hide(toastId), 0);
                      },
                      () => {},
                      'Order Changed',
                      undefined
                    );
                  });
                },
                onError: (error) => {
                  mutationError = error;
                  delay(100).then(() => {
                    const toastId = showToast(
                      () => {
                        setTimeout(() => toast.hide(toastId), 0);
                      },
                      () => {},
                      'Failed to change the order',
                      undefined
                    );
                  });
                },
                refetchQueries: ['listTasks'],
              });
              if (mutationError) throw mutationError;
            }}
            renderSectionHeader={sectionHeader}
            ListHeaderComponent={() => <Box marginTop='none' />}
            renderItem={renderTaskItem}
            renderNewTaskItem={() => (
              <MyTasksNewItem
                onPressTask={(task: Task) => onPress?.(task, false)}
                defaultProject={defaultProject}
                suppressOnboardingTooltip={showFirstTaskTooltip}
              />
            )}
            renderEmptyComponent={renderEmpty}
            renderSectionLoadingComponent={() => (
              <SkeletonList row={(index) => <TaskRowSkeleton key={index} />} />
            )}
            showEmptyMessage={true}
            onSectionEndReached={async (sectionHeader) => {
              await handleSectionEndReached(sectionHeader);
            }}
          />
        )}

        {!grouped && (
          <FlatList
            data={flatTasks}
            renderItem={renderFlatListItem}
            keyExtractor={(item) => item.id.toString()}
            ListFooterComponent={onLoadMore ? renderLoadMoreComponent() : null}
            // Force FlatList to refresh on container resize (RN Web layout changes).
            extraData={layoutTick}
          />
        )}
      </Box>
    </>
  );
};

const styles = StyleSheet.create({
  headerMainContainer: {
    flexDirection: 'row',
    justifyContent: 'flex-start',
    flexWrap: 'nowrap',
    width: '100%',
  },
  bodyMainContainer: {
    flexDirection: 'row',
    backgroundColor: 'white',
    justifyContent: 'flex-start',
    flexWrap: 'nowrap',
    borderBottomWidth: 1,
    width: '100%',
  },
  mainListContainer: {
    flex: 1,
    alignContent: 'center',
    justifyContent: 'flex-start',
  },
});
